/**
 * ZK Raw Attendance Component
 *
 * Displays raw attendance data from ZK devices (like Odoo's Attendance Data view).
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Space,
  Select,
  Tag,
  message,
  Typography,
  Row,
  Col,
  Card,
  Statistic,
  DatePicker,
  Popconfirm,
  Tooltip,
  Input
} from 'antd';
import {
  DeleteOutlined,
  CheckOutlined,
  ReloadOutlined,
  ExportOutlined,
  FilterOutlined,
  UserOutlined,
  WarningOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { zkDeviceApi, zkAttendanceApi, zkMappingApi, ZkDevice, ZkEmployeeMapping, ZkRawAttendance, RawAttendanceStats } from '../../services/zkService';

// ZK devices return local HKT wall-clock times tagged as UTC — render in UTC to avoid double-conversion
dayjs.extend(utc);

const { Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const ZKRawAttendanceComponent: React.FC = () => {
  const [devices, setDevices] = useState<ZkDevice[]>([]);
  const [records, setRecords] = useState<ZkRawAttendance[]>([]);
  const [stats, setStats] = useState<RawAttendanceStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);

  // Filters
  const [filterDevice, setFilterDevice] = useState<string | undefined>();
  const [filterDateRange, setFilterDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>([dayjs().startOf('month'), dayjs().endOf('month')]);
  const [filterSynced, setFilterSynced] = useState<boolean | undefined>();
  const [filterEmployee, setFilterEmployee] = useState<string | undefined>();

  // Employees that can appear as filter options — anyone with at least one
  // ZK mapping (since only mapped employees can have raw attendance records).
  const [employeeOptions, setEmployeeOptions] = useState<Array<{ id: string; label: string }>>([]);

  // Pagination
  const [pagination, setPagination] = useState({ current: 1, pageSize: 50, total: 0 });

  // Fetch devices
  useEffect(() => {
    const fetchDevices = async () => {
      try {
        const data = await zkDeviceApi.getAll(true);
        setDevices(data);
      } catch (error) {
        console.error('Failed to load devices:', error);
      }
    };
    fetchDevices();
  }, []);

  // Fetch employee options from ZK mappings (only employees mapped to a ZK
  // user can produce attendance records, so the picker is scoped to those).
  useEffect(() => {
    const fetchEmployeeOptions = async () => {
      try {
        const mappings: ZkEmployeeMapping[] = await zkMappingApi.getAll();
        const seen = new Map<string, string>();
        for (const m of mappings) {
          if (!m.employeeId || !m.employee) continue;
          if (seen.has(m.employeeId)) continue;
          const fullName = `${m.employee.firstName || ''} ${m.employee.lastName || ''}`.trim();
          const code = m.employee.employeeId ? ` (${m.employee.employeeId})` : '';
          seen.set(m.employeeId, `${fullName || m.zkUserName || m.zkUserId}${code}`);
        }
        setEmployeeOptions(
          [...seen.entries()]
            .map(([id, label]) => ({ id, label }))
            .sort((a, b) => a.label.localeCompare(b.label))
        );
      } catch (error) {
        console.error('Failed to load employee options:', error);
      }
    };
    fetchEmployeeOptions();
  }, []);

  // Fetch records
  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      // Default to current month if no date range set
      const startDate = filterDateRange?.[0]?.startOf('day').toISOString()
        || dayjs().startOf('month').toISOString();
      const endDate = filterDateRange?.[1]?.endOf('day').toISOString()
        || dayjs().endOf('month').toISOString();

      const [recordsData, statsData] = await Promise.all([
        zkAttendanceApi.getAll({
          deviceId: filterDevice,
          employeeId: filterEmployee,
          startDate,
          endDate,
          syncedToHr: filterSynced,
          limit: pagination.pageSize,
          offset: (pagination.current - 1) * pagination.pageSize
        }),
        zkAttendanceApi.getStats({ startDate, endDate })
      ]);

      setRecords(recordsData);
      setStats(statsData);
      setPagination(prev => ({ ...prev, total: statsData.total }));
    } catch (error) {
      message.error('Failed to load attendance records');
      console.error('Error loading records:', error);
    } finally {
      setLoading(false);
    }
  }, [filterDevice, filterEmployee, filterDateRange, filterSynced, pagination.current, pagination.pageSize]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  // Delete selected records
  const handleDeleteSelected = async () => {
    if (selectedRows.length === 0) return;

    try {
      await zkAttendanceApi.delete(selectedRows);
      message.success(`Deleted ${selectedRows.length} record(s)`);
      setSelectedRows([]);
      fetchRecords();
    } catch (error) {
      message.error('Failed to delete records');
    }
  };

  // Mark selected as synced
  const handleMarkAsSynced = async () => {
    if (selectedRows.length === 0) return;

    try {
      await zkAttendanceApi.markAsSynced(selectedRows);
      message.success(`Marked ${selectedRows.length} record(s) as synced`);
      setSelectedRows([]);
      fetchRecords();
    } catch (error) {
      message.error('Failed to mark records');
    }
  };

  // Export to CSV - fetches ALL records matching current filters (not just current page)
  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    setExporting(true);
    try {
      // Fetch all records matching current filters (no pagination limit)
      const allRecords = await zkAttendanceApi.getAll({
        deviceId: filterDevice,
        employeeId: filterEmployee,
        startDate: filterDateRange?.[0]?.startOf('day').toISOString(),
        endDate: filterDateRange?.[1]?.endOf('day').toISOString(),
        syncedToHr: filterSynced,
        limit: 100000,
        offset: 0
      });

      if (allRecords.length === 0) {
        message.warning('No records to export');
        return;
      }

      const headers = ['Punch Time', 'Device', 'ZK User ID', 'Employee', 'Type', 'Verify', 'Synced'];
      const rows = allRecords.map(r => [
        dayjs.utc(r.punchTime).format('YYYY-MM-DD HH:mm:ss'),
        `"${(r.device?.deviceName || '').replace(/"/g, '""')}"`,
        r.zkUserId,
        r.employeeMapping?.employee
          ? `"${r.employeeMapping.employee.firstName} ${r.employeeMapping.employee.lastName}"`
          : `"${(r.employeeMapping?.zkUserName || '').replace(/"/g, '""')}"`,
        r.punchTypeLabel || '',
        r.verifyTypeLabel || '',
        r.syncedToHr ? 'Yes' : 'No'
      ]);

      const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `zk_attendance_${dayjs().format('YYYYMMDD_HHmmss')}.csv`;
      a.click();
      URL.revokeObjectURL(url);

      message.success(`Exported ${allRecords.length} records`);
    } catch (error) {
      message.error('Failed to export records');
      console.error('Export error:', error);
    } finally {
      setExporting(false);
    }
  };

  // Table columns
  const columns: ColumnsType<ZkRawAttendance> = [
    {
      title: 'Punch Time',
      dataIndex: 'punchTime',
      key: 'punchTime',
      width: 180,
      render: (time) => dayjs.utc(time).format('YYYY-MM-DD HH:mm:ss'),
      sorter: (a, b) => dayjs(a.punchTime).valueOf() - dayjs(b.punchTime).valueOf()
    },
    {
      title: 'Device',
      key: 'device',
      width: 150,
      render: (_, record) => record.device?.deviceName || '-'
    },
    {
      title: 'ZK User',
      dataIndex: 'zkUserId',
      key: 'zkUserId',
      width: 100,
      render: (id) => <Text code>{id}</Text>
    },
    {
      title: 'Employee',
      key: 'employee',
      width: 200,
      render: (_, record) => {
        if (record.employeeMapping?.employee) {
          const emp = record.employeeMapping.employee;
          return (
            <Space>
              <UserOutlined />
              {emp.firstName} {emp.lastName}
            </Space>
          );
        }
        if (record.employeeMapping?.zkUserName) {
          return (
            <Tooltip title="User not mapped to employee">
              <Space>
                <WarningOutlined style={{ color: '#faad14' }} />
                <Text type="secondary">{record.employeeMapping.zkUserName}</Text>
              </Space>
            </Tooltip>
          );
        }
        return (
          <Tooltip title="Unknown user - not mapped">
            <Tag color="warning" icon={<WarningOutlined />}>Unknown</Tag>
          </Tooltip>
        );
      }
    },
    {
      title: 'Type',
      dataIndex: 'punchTypeLabel',
      key: 'punchType',
      width: 100,
      render: (label, record) => {
        if (!label) return '-';
        const colors: Record<string, string> = {
          'Check-in': 'green',
          'Check-out': 'blue',
          'Break-out': 'orange',
          'Break-in': 'cyan',
          'OT-in': 'purple',
          'OT-out': 'magenta'
        };
        return <Tag color={colors[label] || 'default'}>{label}</Tag>;
      }
    },
    {
      title: 'Verify',
      dataIndex: 'verifyTypeLabel',
      key: 'verifyType',
      width: 100,
      render: (label) => label || '-'
    },
    {
      title: 'Synced',
      dataIndex: 'syncedToHr',
      key: 'syncedToHr',
      width: 80,
      align: 'center',
      render: (synced, record) => (
        <Tooltip title={synced ? `Synced at ${dayjs(record.syncedAt).format('YYYY-MM-DD HH:mm')}` : 'Not yet synced'}>
          {synced ? (
            <Tag color="success" icon={<CheckOutlined />}>Yes</Tag>
          ) : (
            <Tag>No</Tag>
          )}
        </Tooltip>
      )
    }
  ];

  // Row selection
  const rowSelection = {
    selectedRowKeys: selectedRows,
    onChange: (keys: React.Key[]) => setSelectedRows(keys as string[])
  };

  return (
    <div>
      {/* Stats */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Total Records"
              value={stats?.total || 0}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Synced"
              value={stats?.synced || 0}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Pending"
              value={stats?.pending || 0}
              valueStyle={{ color: (stats?.pending || 0) > 0 ? '#1890ff' : undefined }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Unmapped"
              value={stats?.unmapped || 0}
              valueStyle={{ color: (stats?.unmapped || 0) > 0 ? '#faad14' : undefined }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col>
            <Space>
              <FilterOutlined />
              <Text strong>Filters:</Text>
            </Space>
          </Col>
          <Col>
            <Select
              style={{ width: 150 }}
              placeholder="Device"
              allowClear
              value={filterDevice}
              onChange={setFilterDevice}
            >
              {devices.map(d => (
                <Option key={d.id} value={d.id}>{d.deviceName}</Option>
              ))}
            </Select>
          </Col>
          <Col>
            <Select
              style={{ width: 220 }}
              placeholder="Employee"
              allowClear
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                ((option?.children as any) || '').toLowerCase().includes(input.toLowerCase())
              }
              value={filterEmployee}
              onChange={setFilterEmployee}
            >
              {employeeOptions.map(e => (
                <Option key={e.id} value={e.id}>{e.label}</Option>
              ))}
            </Select>
          </Col>
          <Col>
            <RangePicker
              value={filterDateRange}
              onChange={(dates) => setFilterDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null)}
            />
          </Col>
          <Col>
            <Select
              style={{ width: 120 }}
              placeholder="Synced"
              allowClear
              value={filterSynced}
              onChange={setFilterSynced}
            >
              <Option value={true}>Synced</Option>
              <Option value={false}>Not Synced</Option>
            </Select>
          </Col>
          <Col flex="auto" style={{ textAlign: 'right' }}>
            <Space>
              <Button
                icon={<ExportOutlined />}
                onClick={handleExport}
                loading={exporting}
                disabled={records.length === 0}
              >
                Export CSV
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={fetchRecords}
                loading={loading}
              >
                Refresh
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Bulk Actions */}
      {selectedRows.length > 0 && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Space>
            <Text>{selectedRows.length} record(s) selected</Text>
            <Popconfirm
              title="Delete selected records?"
              onConfirm={handleDeleteSelected}
              okText="Delete"
              okButtonProps={{ danger: true }}
            >
              <Button danger icon={<DeleteOutlined />} size="small">
                Delete Selected
              </Button>
            </Popconfirm>
            <Button
              icon={<CheckOutlined />}
              size="small"
              onClick={handleMarkAsSynced}
            >
              Mark as Synced
            </Button>
            <Button size="small" onClick={() => setSelectedRows([])}>
              Clear Selection
            </Button>
          </Space>
        </Card>
      )}

      {/* Table */}
      <Table
        columns={columns}
        dataSource={records}
        rowKey="id"
        loading={loading}
        rowSelection={rowSelection}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} records`,
          onChange: (page, pageSize) => setPagination(prev => ({ ...prev, current: page, pageSize }))
        }}
        size="middle"
        scroll={{ x: 1100 }}
      />
    </div>
  );
};

export default ZKRawAttendanceComponent;
